import { useEditModalStore } from '../../model/use-edit-modal.store';
import { warrantyLogContext } from '../../model/warranty-log-context';
import { SelectManufacturers } from '../select-manufacturers/select-manufacturers';
import { SelectStatus } from '../select-status/select-status';
import { SelectWaStatus } from '../select-wa-status/select-wa-status';
import { SelectWarrantyType } from '../select-warranty-type/select-warranty-type';
import { ActionPanel } from './action-panel/action-panel';
import { LogTable } from './log-table/log-table';
import { Notes } from './notes/notes';
import styles from './styles.module.scss';
import { UploadPdf } from './upload-pdf/upload-pdf';
import { WarrantyLogEditInput, WarrantyLogNoteType } from 'api-new/generated';
import clsx from 'clsx';
import { useQueryResponse } from 'components/table/contexts/query-response';
import { useWarrantyLogByIdQuery, useWarrantyLogEditMutation } from 'entities/warranty';
import { formatErrorMessage, QueryError } from 'features/shared/model';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Button, SkeletonWrapper } from 'shared';

export const ModalBody = () => {
  const state = useEditModalStore((s) => s.state);
  const closeModal = useEditModalStore((s) => s.closeModal);
  const setLoading = useEditModalStore((s) => s.setLoading);
  const setState = useEditModalStore((s) => s.setState);
  const { refetch } = useQueryResponse(warrantyLogContext);

  const id = state?.id;

  const { data, isFetching } = useWarrantyLogByIdQuery(
    {
      id: id as number
    },
    undefined,
    {
      enabled: Boolean(id)
    }
  );

  const { mutate, isLoading } = useWarrantyLogEditMutation({
    onSuccess: () => {
      toast.success('Successfully update!');
      refetch();
      closeModal();
    },
    onError: (error: QueryError) => {
      const message = formatErrorMessage(error);
      toast.error(message);
      console.error(message);
    }
  });

  const {
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { dirtyFields }
  } = useForm<WarrantyLogEditInput>({
    defaultValues: {
      branch: undefined,
      warrantyTypeId: undefined,
      wipNo: -1,
      regNo: '',
      claimNo: '',
      manufactureId: undefined,
      waStatus: undefined,
      status: undefined
    }
  });

  useEffect(() => {
    if (!data) return;

    setState({
      branch: data.branchId,
      regNo: data.regno,
      wipNo: data.wip
    });

    reset({
      branch: data.branchId,
      warrantyTypeId: data.warrantyTypeId ?? undefined,
      wipNo: data.wip,
      regNo: data.regno,
      claimNo: data.claimNo,
      manufactureId: data.manufactureId,
      waStatus: data.waStatus,
      status: data.status
    });
  }, [data, reset, setState]);

  useEffect(() => {
    setLoading(isFetching);
  }, [isFetching, setLoading]);

  const onSubmit = (value: WarrantyLogEditInput) => {
    if (!data) return;

    const original: WarrantyLogEditInput = {
      branch: data.branchId,
      warrantyTypeId: data.warrantyTypeId ?? undefined,
      wipNo: data.wip,
      regNo: data.regno,
      claimNo: data.claimNo,
      manufactureId: data.manufactureId,
      waStatus: data.waStatus,
      status: data.status
    };

    const changedValues = Object.fromEntries(
      (Object.keys(value) as Array<keyof WarrantyLogEditInput>)
        .filter((k) => {
          const current = value[k] ?? undefined;
          const prev = original[k] ?? undefined;
          return current !== prev;
        })
        .map((k) => [k, value[k]])
    ) as Partial<WarrantyLogEditInput>;

    if (Object.keys(changedValues).length === 0) {
      return;
    }

    mutate({
      id: state!.id!,
      requestBody: { ...changedValues }
    });
  };

  const historyNotes = useMemo(() => data?.historyNotes ?? [], [data?.historyNotes]);
  const waNotes = useMemo(() => data?.waNotes ?? [], [data?.waNotes]);
  const files = useMemo(() => data?.files ?? [], [data?.files]);

  const warrantyTypeId = watch('warrantyTypeId');
  const waStatus = watch('waStatus');
  const status = watch('status');

  return (
    <div className={clsx(styles.modalBody, 'container', 'p-0')}>
      <div className={clsx(styles.mainRow, 'row m-0')}>
        <div className="col-4 ps-8 py-4 pe-4">
          <form>
            <div>
              <SkeletonWrapper isShow={isFetching} height={60}>
                <LogTable data={data} />
              </SkeletonWrapper>
            </div>
            <div className="mt-6">
              <SkeletonWrapper isShow={isFetching} height={40}>
                <ActionPanel soHeaderId={data?.soHeaderId} />
              </SkeletonWrapper>
            </div>
            <div className="mt-6">
              <SkeletonWrapper isShow={isFetching} height={40}>
                <label className="form-label">Warranty Type</label>
                <SelectWarrantyType
                  warrantyTypeIds={warrantyTypeId != undefined ? [warrantyTypeId] : undefined}
                  onChange={(val) => setValue('warrantyTypeId', val?.[0], { shouldDirty: true })}
                  placeholder="Select"
                  isClearable={false}
                  isMulti={false}
                />
              </SkeletonWrapper>
            </div>
            <div className="mt-6">
              <SkeletonWrapper isShow={isFetching} height={40}>
                <label className="form-label">Manufacturer</label>
                <SelectManufacturers
                  manufacturerId={watch('manufactureId') ?? undefined}
                  onChange={(val) => setValue('manufactureId', val, { shouldDirty: true })}
                  placeholder="Select"
                  isClearable={false}
                />
              </SkeletonWrapper>
            </div>
            <div className="mt-6">
              <SkeletonWrapper isShow={isFetching} height={40}>
                <label className="form-label">WA Status</label>
                <SelectWaStatus
                  statuses={waStatus != undefined ? [waStatus] : undefined}
                  onChange={(val) => setValue('waStatus', val?.[0], { shouldDirty: true })}
                  placeholder="Select"
                  isClearable={false}
                  isMulti={false}
                />
              </SkeletonWrapper>
            </div>
            <div className="mt-6">
              <SkeletonWrapper isShow={isFetching} height={40}>
                <label className="form-label">Status</label>
                <SelectStatus
                  statuses={status != undefined ? [status] : undefined}
                  onChange={(val) => setValue('status', val?.[0], { shouldDirty: true })}
                  placeholder="Select"
                  isClearable={false}
                  isMulti={false}
                />
              </SkeletonWrapper>
            </div>
            <div className="mt-6">
              <SkeletonWrapper isShow={isFetching} height={60}>
                <UploadPdf id={data?.id} files={files} />
              </SkeletonWrapper>
            </div>
          </form>
        </div>
        <div className={clsx('col-4 p-4', styles.borderY)}>
          <Notes id={data?.id} noteType={WarrantyLogNoteType.Note} notes={historyNotes} status={status} />
        </div>
        <div className="col-4 ps-4 py-4 pe-8">
          <Notes id={data?.id} noteType={WarrantyLogNoteType.WaNote} notes={waNotes} status={status} />
        </div>
      </div>
      <div className="row p-0 m-0">
        <div className="col-12 px-8 pt-4">
          <div className="d-flex gap-6 flex-nowrap align-items-center">
            <Button
              text="Save changes"
              additionalClass={styles.actionBtn}
              size="sm"
              disabled={isFetching}
              isLoading={isLoading}
              onClick={handleSubmit(onSubmit)}
            />
            <Button
              text="Close"
              variant="light-secondary"
              additionalClass={styles.actionBtn}
              size="sm"
              onClick={closeModal}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
